import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { StyledAddCollectionButton } from "components/buttons/AddCollectionButton";
import CustomInput from "components/UI/CustomInput";
import TopMenu from "components/UI/TopMenu";
import colors, { sizes } from "theme/themeConstants";
import tick from "components/icons/tick.svg";
import MarkOkIcon from "components/icons/Components/MarkOkIcon";
import DeleteIcon from "components/icons/Components/DeleteIcon";
import { IAppState } from "store/app-state";
import { fetchCurrentUser, updateUserProfile } from "store/user/user-action";
import { IOperation } from "services/RedGifsService";
import { PROFILE_UPDATE_FIELDS_PATHS } from "utils/constants";
import DeleteAccountPopup from "components/UI/Popups/DeleteAccountPopup";
import { FooterAds } from "../../components/ads/FooterAds";
import { BackButton } from "components/buttons";
import { routes } from "routes";

const MOCK_TOPIC = ["Account Info", "Profile"];
const PREFENCES = [
  "straight", "gay", "lesbian", "trans", "animated"
];

const MIN_PASS_LENGTH = 8;
const MAX_PASS_LENGTH = 20;

export const AccountSettingsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const currentUser = useSelector((state: IAppState) => state.user.currentUser);

  const { handleSubmit, control, trigger, watch, reset, setValue } = useForm();

  const [preferences, setPrefences] = useState<string[]>([]);
  const [passLengthIsOk, setPassLengthIsOk] = useState(false);
  const [passSymbolsIsOk, setPassSymbolsIsOk] = useState(false);

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const handleDeleteModal = () => {
    setIsOpenDeleteModal(prev => !prev);
  };

  const setCurrentTopic = (topicId: string) => {
    if (topicId === MOCK_TOPIC[0]) {
      navigate("/settings/account");
    } else {
      navigate("/settings/profile");
    }
  };

  const onSubmit = (data: any) => {
    const sendedData: IOperation[] = [
      { op: "add", path: PROFILE_UPDATE_FIELDS_PATHS.email, value: data.email },
      { op: "add", path: PROFILE_UPDATE_FIELDS_PATHS.preferences, value: data.preferences }
    ];

    sendedData.push({
      op: "add",
      path: PROFILE_UPDATE_FIELDS_PATHS.password,
      value: {
        new: data.new_password
      }
    });

    console.debug("UPDATE", sendedData);

    dispatch(updateUserProfile(sendedData));
  };

  const handleClick = (value: string) => {
    const newPreferences = [...preferences];
    let isExist = false;

    newPreferences.forEach((el, index) => {
      if (value === el) {
        isExist = true;
        if (newPreferences.length === 1) {
          return;
        }

        newPreferences.splice(index, 1);
      }
    });

    if (!isExist) {
      newPreferences.push(value);
    }

    setPrefences(newPreferences);
    setValue("preferences", newPreferences);
  };

  const isSelected = (value: string) => {
    const newPreferences = [...preferences];
    let isExist = false;

    newPreferences.forEach(el => {
      if (value === el) {
        isExist = true;
      }
    });

    if (!isExist) {
      return false;
    } else {
      return true;
    }
  };

  const validate = (val: string) => {
    // OK not to change password.
    if (val.length === 0) {
      return true;
    }

    if (val.length < MIN_PASS_LENGTH || val.length > MAX_PASS_LENGTH) {
      setPassLengthIsOk(false);
    } else {
      setPassLengthIsOk(true);
    }

    if (val.match(/[a-z]+/) === null) {
      setPassSymbolsIsOk(false);
    } else if (val.match(/[A-Z]+/) === null) {
      setPassSymbolsIsOk(false);
    } else if (val.match(/[0-9]+/) === null) {
      setPassSymbolsIsOk(false);
    } else {
      setPassSymbolsIsOk(true);
    }
  };

  const getUserInfo = () => {
    dispatch(fetchCurrentUser());
  };

  useEffect(() => {
    getUserInfo();
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (currentUser) {
      reset({
        email: currentUser.email,
        preferences: currentUser.preferences
      });
      setPrefences(currentUser.preferences ? currentUser.preferences : []);
    }
    // eslint-disable-next-line
  }, [currentUser])

  return (
    <Fragment>
      <Container className="wide page accountSettingsPage">
        {isOpenDeleteModal && <DeleteAccountPopup isOpen={isOpenDeleteModal} togglePopup={handleDeleteModal}/>}
        <div className="TopHeader">
          <BackButton
            link={routes.user(currentUser.username)}
          />
          <Title>Settings</Title>
        </div>
        <TopMenu
          topicValues={MOCK_TOPIC}
          currentTopic={MOCK_TOPIC[0]}
          setCurrentTopic={setCurrentTopic}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Section>
            <SubTitle>Change email</SubTitle>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              rules={{
                required: {
                  value: true,
                  message: "Required Field!"
                },
                pattern: {
                  value: /^[A-Za-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}/,
                  message: "Invalid Email!"
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Block>
                  <label>Email</label>
                  <CustomInput
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : ""}
                    fullWidth
                  />
                  <span>You will have to follow the emailed link to actually change your email address.</span>
                </Block>
              )}
            />
          </Section>
          <Section>
            <SubTitle>Select preferences</SubTitle>
            <Label>Preferences</Label>
            <PreferencesWrapper>
              {PREFENCES.map((el, index) => (
                <Option
                  key={el}
                  sx={{
                    borderRight: (index === 0 || index === 2 || index === 4) ? " 1px solid rgba(255, 255, 255, 0.14)" : "none",
                    paddingRight: (index === 0 || index === 2 || index === 4) ? "1rem" : "0",
                    paddingLeft: (index === 0 || index === 2 || index === 4) ? 0 : "1rem"
                  }}
                >
                  <p>{el}</p>
                  <StyledAddCollectionButton
                    onClick={() => handleClick(el)}
                    isButtonActive={isSelected(el)}
                  >
                    {isSelected(el) && <img src={tick} alt="selected"/>}
                  </StyledAddCollectionButton>
                </Option>
              ))}
            </PreferencesWrapper>
          </Section>
          <Section>
            <SubTitle>Change password</SubTitle>
            <Controller
              name="new_password"
              control={control}
              defaultValue=""
              rules={{
                // required: {
                //   value: true,
                //   message: "Required Field!"
                // },
                validate: (value: string): boolean | string => {
                  if (value.length === 0) {
                    return true; // keep the old password
                  }

                  if (value.length < MIN_PASS_LENGTH || value.length > MAX_PASS_LENGTH) {
                    return `Please use ${MIN_PASS_LENGTH} to ${MAX_PASS_LENGTH} symbols.`;
                  }

                  if (value.match(/[a-z]+/) === null) {
                    return "Please add lower case letters.";
                  }

                  if (value.match(/[A-Z]+/) === null) {
                    return "Please add upper case letters.";
                  }

                  if (value.match(/[0-9]+/) === null) {
                    return "Please add numbers.";
                  }

                  return true;
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Block sx={{ marginTop: "1rem" }}>
                  <label>New password</label>
                  <CustomInput
                    value={value}
                    type="password"
                    onChange={async (e) => {
                      onChange(e);
                      validate(e.target.value);
                      await trigger("new_password");
                    }}
                    error={!!error}
                    // helperText={error ? error.message : ""}
                    fullWidth
                  />
                  <label style={{ fontSize: "10px" }}>Password must have:</label>
                  <FlexBlock>
                    <MarkOkIcon color={passLengthIsOk ? "green" : "#FF536D"}/>
                    <span>{MIN_PASS_LENGTH} to {MAX_PASS_LENGTH} characters</span>
                  </FlexBlock>
                  <FlexBlock>
                    <MarkOkIcon color={passSymbolsIsOk ? "green" : "#FF536D"}/>
                    <span>Upper case and lower case letters, numbers</span>
                  </FlexBlock>
                </Block>
              )}
            />
            <Controller
              name="password_confirm"
              control={control}
              defaultValue=""
              rules={{
                validate: {
                  value: (value) => value === watch("new_password") || "Passwords do not match!"
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Block sx={{ marginTop: "1rem" }}>
                  <label>Repeat password</label>
                  <CustomInput
                    value={value}
                    type="password"
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : ""}
                    fullWidth
                  />
                  <label style={{ fontSize: "10px" }}>We will send you an email asking to confirm this change.</label>
                </Block>
              )}
            />
          </Section>
          <Button variant="contained" fullWidth sx={{
            background: colors.purpleGradient,
            margin: "2rem 0 0rem 0",
            minHeight: "3rem",
            borderRadius: "38px",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }} type="submit">
            <span>Save Changes</span>
          </Button>
        </form>
        <Button sx={{
          margin: "auto",
          marginTop: "25px",
          color: colors.white,
          display: "flex",
          gap: "8px"
        }} onClick={handleDeleteModal}>
          <DeleteIcon/>
          <span>Delete account</span>
        </Button>
      </Container>

      <FooterAds/>
    </Fragment>
  );
};

const Container = styled("div")({
  maxWidth: sizes.containerSize,
  width: "100%",
  paddingTop: "27px",
  display: "flex",
  flexDirection: "column",
  marginBottom: "64px",
  "*": {
    fontFamily: "'Poppins', sans-serif!important"
  },
  ".TopHeader": {
    display: "flex",
    alignItems: "center",
    marginBottom: "18px",
    ".back svg": {
      width: "24px",
      height: "auto"
    }
  },
  "& form": {
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
    marginTop: "1rem"
  },
  "@media screen and (max-width: 767px)": {
    padding: "1rem",
    boxSizing: "border-box"
  }
});

const Title = styled("h3")({
  fontWeight: 500,
  fontSize: "20px",
  marginLeft: "8px"
});

const SubTitle = styled("h4")({
  fontWeight: 500,
  fontSize: "16px",
  marginBottom: "1rem"
});

const Section = styled("div")({
});

const Block = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "& label": {
    fontSize: "12px",
    color: colors.white,
    opacity: 0.8
  },
  "& input": {
    color: colors.white
  },
  "& span": {
    fontWeight: 500,
    fontSize: "10px",
    opacity: 0.5
  }
});

const PreferencesWrapper = styled("div")({
  marginTop: "13px",
  display: "flex",
  flexWrap: "wrap",
  borderRadius: "12px",
  background: colors.cardBackgroundDarkBlue,
  paddingLeft: "1rem"
});

const Label = styled("label")({
  fontWeight: 500,
  fontSize: "12px"
});

const Option = styled("div")({
  width: "48.5%",
  height: "50px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "& p": {
    fontWeight: 500,
    fontSize: "14px",
    textTransform: "capitalize !important",
  }
});

const FlexBlock = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "& span": {
    fontSize: "10px",
    color: colors.white,
    opacity: 0.5
  }
});
